import { useState } from "react";
import { Link } from "react-router-dom";
import {
  LayoutDashboard, CalendarCheck, CalendarDays, Tag, Star, MessageSquare,
  Settings, LogOut, ExternalLink, Menu, Gift, Images, CalendarRange, BadgePercent, Mail, BellRing, CalendarHeart, Users,
} from "lucide-react";
import { Saguaro } from "@/components/Saguaro";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Overview, Bookings, CalendarSync, Promos, Reviews, Inquiries, Content, Upsells, Photos, Rates, Offers, Messaging, Reminders, Events, Affiliates,
} from "@/components/admin/sections";

const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard, C: Overview },
  { key: "bookings", label: "Bookings", icon: CalendarCheck, C: Bookings },
  { key: "calendar", label: "Calendar & Sync", icon: CalendarDays, C: CalendarSync },
  { key: "rates", label: "Rates & Seasons", icon: CalendarRange, C: Rates },
  { key: "events", label: "Local Events", icon: CalendarHeart, C: Events },
  { key: "inquiries", label: "Inquiries", icon: MessageSquare, C: Inquiries },
  { key: "offers", label: "Special Offers", icon: BadgePercent, C: Offers },
  { key: "messaging", label: "Guest Messages", icon: Mail, C: Messaging },
  { key: "reminders", label: "Guest Reminders", icon: BellRing, C: Reminders },
  { key: "promos", label: "Promo Codes", icon: Tag, C: Promos },
  { key: "affiliates", label: "Affiliates", icon: Users, C: Affiliates },
  { key: "upsells", label: "Add-ons", icon: Gift, C: Upsells },
  { key: "photos", label: "Photos", icon: Images, C: Photos },
  { key: "reviews", label: "Reviews", icon: Star, C: Reviews },
  { key: "content", label: "Content & Pricing", icon: Settings, C: Content },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [active, setActive] = useState("overview");
  const [mobileOpen, setMobileOpen] = useState(false);
  const ActiveComp = NAV.find((n) => n.key === active)?.C || Overview;

  const Sidebar = () => (
    <aside className="w-64 bg-foreground text-white/80 flex flex-col h-full">
      <div className="p-6">
        <div className="flex items-center gap-2 font-serif text-2xl font-semibold text-white">
          <Saguaro className="h-6 w-6 shrink-0" /> The East Valley
        </div>
        <div className="text-xs text-white/40 mt-1">Owner Dashboard</div>
      </div>
      <nav className="flex-1 min-h-0 overflow-y-auto px-3 space-y-1">
        {NAV.map((n) => (
          <button
            key={n.key}
            data-testid={`nav-${n.key}`}
            onClick={() => { setActive(n.key); setMobileOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              active === n.key ? "bg-primary text-white" : "hover:bg-white/10 text-white/70"
            }`}
          >
            <n.icon className="h-4.5 w-4.5" strokeWidth={1.5} /> {n.label}
          </button>
        ))}
      </nav>
      <div className="p-3 border-t border-white/10 space-y-1">
        <Link to="/" target="_blank" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/70 hover:bg-white/10 transition-colors">
          <ExternalLink className="h-4 w-4" /> View site
        </Link>
        <button onClick={logout} data-testid="logout-btn" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/70 hover:bg-white/10 transition-colors">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-[#F3F2F0] flex">
      <div className="hidden md:block fixed inset-y-0"><Sidebar /></div>

      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="w-64 h-full"><Sidebar /></div>
          <div className="flex-1 bg-black/40" onClick={() => setMobileOpen(false)} />
        </div>
      )}

      <div className="flex-1 min-w-0 md:ml-64">
        <div className="md:hidden flex items-center justify-between bg-foreground text-white px-4 h-14">
          <span className="font-serif text-xl">The East Valley</span>
          <button onClick={() => setMobileOpen(true)} data-testid="admin-mobile-menu"><Menu /></button>
        </div>
        <main className="p-5 md:p-10 max-w-6xl w-full min-w-0">
          <ActiveComp />
        </main>
      </div>
    </div>
  );
}
